import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from '@course-authoring/i18n';

export interface VideoTranscriptDownload {
  blob: Blob;
  filename: string;
}

export interface VideoTranscriptOperations {
  checkAvailability: (videoId: string, locale: string, signal: AbortSignal) => Promise<boolean>;
  download: (videoId: string, locale: string) => Promise<VideoTranscriptDownload>;
}

interface UseVideoTranscriptOptions {
  videoUrl?: string;
  videoId?: string;
  locale?: string;
  operations?: VideoTranscriptOperations;
}

interface UseVideoTranscriptResult {
  transcriptAvailable: boolean;
  isDownloadingTranscript: boolean;
  transcriptDownloadError: string | null;
  handleTranscriptDownload: () => void;
}

export function useVideoTranscript({
  videoUrl,
  videoId,
  locale,
  operations,
}: UseVideoTranscriptOptions): UseVideoTranscriptResult {
  const { t } = useTranslation();
  const [transcriptAvailable, setTranscriptAvailable] = useState(false);
  const [isDownloadingTranscript, setIsDownloadingTranscript] = useState(false);
  const [transcriptDownloadError, setTranscriptDownloadError] = useState<string | null>(null);

  useEffect(() => {
    setTranscriptAvailable(false);
    setTranscriptDownloadError(null);

    if (!videoUrl || !videoId || !locale || !operations) return;

    const controller = new AbortController();
    void operations
      .checkAvailability(videoId, locale, controller.signal)
      .then((exists) => {
        if (!controller.signal.aborted) setTranscriptAvailable(exists);
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        console.error('[Transcript] availability check failed', error);
        setTranscriptAvailable(false);
      });

    return () => controller.abort();
  }, [locale, operations, videoId, videoUrl]);

  const handleTranscriptDownload = useCallback(() => {
    if (isDownloadingTranscript || !transcriptAvailable || !videoId || !locale || !operations) {
      return;
    }

    setTranscriptDownloadError(null);
    setIsDownloadingTranscript(true);
    void operations
      .download(videoId, locale)
      .then(({ blob, filename }) => {
        const objectUrl = URL.createObjectURL(blob);
        try {
          const anchor = document.createElement('a');
          anchor.href = objectUrl;
          anchor.download = filename;
          document.body.appendChild(anchor);
          anchor.click();
          anchor.remove();
        } finally {
          URL.revokeObjectURL(objectUrl);
        }
      })
      .catch((error: unknown) => {
        console.error('[Transcript] download failed', error);
        setTranscriptDownloadError(t('video.transcript_download_failed'));
      })
      .finally(() => setIsDownloadingTranscript(false));
  }, [isDownloadingTranscript, locale, operations, t, transcriptAvailable, videoId]);

  return {
    transcriptAvailable,
    isDownloadingTranscript,
    transcriptDownloadError,
    handleTranscriptDownload,
  };
}
